<!-- src/components/device/commands/ChoixBinaire.vue -->
<!--
  Réglage à deux états, partagé admin/client (GPS, entrée filaire). Une seule règle, pour
  qu'on ne puisse jamais confondre « activer » et « désactiver » :
    - deux options NOMMÉES dans un même groupe, jamais deux boutons d'action côte à côte ;
    - l'option cochée est ce que la carte RAPPORTE, pas ce qu'on lui a demandé ;
    - demandé ≠ rapporté : badge « en attente de la carte » (carte qui dort, accusé pas reçu) ;
    - jamais rapporté : rien n'est coché et on le dit, plutôt qu'un faux « désactivé ».
-->
<template>
  <div>
    <div class="inline-flex flex-wrap rounded-md border border-gray-300 overflow-hidden" role="group"
         :aria-label="titre">
      <button v-for="o in options" :key="o.cmd" type="button" :disabled="busy"
              :aria-pressed="o.coche ? 'true' : 'false'" @click="choisir(o)"
              :class="['px-3 py-1.5 text-sm flex items-center gap-1.5',
                       o.coche ? 'bg-primary-600 text-white font-semibold' : 'bg-white text-gray-600 hover:bg-gray-50']">
        <svg v-if="o.coche" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {{ o.label }}
      </button>
    </div>
    <div v-if="enAttente" class="mt-1">
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        {{ $t('reglage.enAttente', { choix: demande ? labelOui : labelNon }) }}
      </span>
    </div>
    <p v-else-if="rapporte === null" class="mt-1 text-xs text-gray-500">{{ $t('reglage.inconnu') }}</p>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';

// Le firmware rapporte tantôt `true`, tantôt `1` ou `"enabled"` : même lecture que le serveur
// (asReportedBool, mqtt.service.js). Absent = jamais rapporté, donc inconnu.
export const lireRapporte = v => (v === undefined || v === null) ? null
  : (v === true || v === 1 || v === '1' || v === 'enabled');

export default defineComponent({
  name: 'ChoixBinaire',
  props: {
    titre:    { type: String, default: '' },
    rapporte: { default: null },               // valeur brute du rapport de la carte
    demande:  { default: null },               // colonne désirée du device
    labelOui: { type: String, required: true },
    labelNon: { type: String, required: true },
    cmdOui:   { type: String, required: true },
    cmdNon:   { type: String, required: true },
    ouiDabord:{ type: Boolean, default: false },  // ordre d'affichage des deux options
    send:     { type: Function, required: true },
    busy:     { type: Boolean, default: false }
  },
  setup(props) {
    const rapporte = computed(() => lireRapporte(props.rapporte));
    const demande  = computed(() => props.demande === null || props.demande === undefined
      ? null : !!props.demande);
    const enAttente = computed(() => rapporte.value !== null && demande.value !== null
      && rapporte.value !== demande.value);
    const options = computed(() => {
      const oui = { cmd: props.cmdOui, valeur: true,  label: props.labelOui, coche: rapporte.value === true };
      const non = { cmd: props.cmdNon, valeur: false, label: props.labelNon, coche: rapporte.value === false };
      return props.ouiDabord ? [oui, non] : [non, oui];
    });
    // Toucher l'option déjà en vigueur, et déjà demandée, ne renvoie rien.
    const choisir = o => {
      if (o.coche && demande.value === o.valeur) return;
      props.send(o.cmd);
    };
    return { rapporte, demande, enAttente, options, choisir };
  }
});
</script>
